/**
 * WhatsApp Configuration Page
 * Two tabs: Instances (connect/manage) + Messages (view/retry)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Badge,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormLabel,
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  Add as AddIcon,
  LinkOff as DisconnectIcon,
  Delete as DeleteIcon,
  QrCode as QrCodeIcon,
  Refresh as RetryIcon,
  Cancel as CancelIcon,
  Check as CheckIcon,
  Message as MessageIcon,
  PhoneAndroid as InstanceIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import {
  PageShell,
  DataTable,
  type DataTableColumn,
  type DataTableAction,
} from '../../components';
import { useManifesto } from '../../stores/manifestoStore';
import { useAcao } from '../../authorization/useVeredito';
import { ActionButton } from '../../components/common';
import whatsappService from '../../services/whatsappService';
import { providerService } from '../../services/providerService';
import { companyService } from '../../services/companyService';
import type {
  WhatsAppInstance,
  WhatsAppInstanceStatus,
  WhatsAppMessageItem,
  WhatsAppMessageStatus,
} from '../../types/whatsapp';

// ============================================================================
// TAB PANEL
// ============================================================================

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`whatsapp-tabpanel-${index}`}
      aria-labelledby={`whatsapp-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

// ============================================================================
// STATUS HELPERS
// ============================================================================

const instanceStatusConfig: Record<WhatsAppInstanceStatus, { label: string; color: 'success' | 'warning' | 'error' | 'info' | 'default' }> = {
  connected: { label: 'Conectado', color: 'success' },
  qr_pending: { label: 'Aguardando QR', color: 'warning' },
  connecting: { label: 'Conectando...', color: 'info' },
  disconnected: { label: 'Desconectado', color: 'default' },
};

const messageStatusConfig: Record<WhatsAppMessageStatus, { label: string; color: 'success' | 'warning' | 'error' | 'info' | 'default' }> = {
  pending: { label: 'Pendente', color: 'warning' },
  sending: { label: 'Enviando', color: 'info' },
  sent: { label: 'Enviada', color: 'success' },
  delivered: { label: 'Entregue', color: 'success' },
  failed: { label: 'Falha', color: 'error' },
  cancelled: { label: 'Cancelada', color: 'default' },
};

const resolveInstanceStatus = (inst: WhatsAppInstance): WhatsAppInstanceStatus =>
  inst.liveStatus || inst.status;

// ============================================================================
// TABLE COLUMNS
// ============================================================================

const instanceColumns: DataTableColumn<WhatsAppInstance>[] = [
  {
    id: 'displayName',
    label: 'Nome',
    primary: true,
    render: (inst) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WhatsAppIcon sx={{ color: '#25D366', fontSize: 20 }} />
        <Typography variant="body2" fontWeight="medium">
          {inst.displayName}
        </Typography>
      </Box>
    ),
  },
  {
    id: 'entityType',
    label: 'Tipo',
    render: (inst) => (
      <Chip
        label={inst.isGlobal ? 'Global' : inst.entityType === 'company' ? 'Empresa' : 'Clínica'}
        size="small"
        variant={inst.isGlobal ? 'filled' : 'outlined'}
        color={inst.isGlobal ? 'secondary' : inst.entityType === 'company' ? 'info' : 'default'}
      />
    ),
  },
  {
    id: 'phoneNumber',
    label: 'Telefone',
    render: (inst) => inst.phoneNumber || '—',
  },
  {
    id: 'status',
    label: 'Status',
    render: (inst) => {
      const cfg = instanceStatusConfig[resolveInstanceStatus(inst)] || instanceStatusConfig.disconnected;
      return <Chip label={cfg.label} color={cfg.color} size="small" />;
    },
  },
  {
    id: 'lastConnectedAt',
    label: 'Última Conexão',
    render: (inst) =>
      inst.lastConnectedAt ? new Date(inst.lastConnectedAt).toLocaleString('pt-BR') : '—',
  },
];

const messageColumns: DataTableColumn<WhatsAppMessageItem>[] = [
  {
    id: 'recipientPhone',
    label: 'Destinatário',
    primary: true,
    render: (msg) => msg.recipientPhone,
  },
  {
    id: 'messageType',
    label: 'Tipo',
    render: (msg) => <Chip label={msg.messageType} size="small" variant="outlined" />,
  },
  {
    id: 'content',
    label: 'Conteúdo',
    render: (msg) => (
      <Box
        sx={{
          maxWidth: 200,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {msg.content}
      </Box>
    ),
  },
  {
    id: 'status',
    label: 'Status',
    render: (msg) => {
      const statusCfg = messageStatusConfig[msg.status] || messageStatusConfig.pending;
      return <Chip label={statusCfg.label} color={statusCfg.color} size="small" />;
    },
  },
  {
    id: 'attempts',
    label: 'Tentativas',
    render: (msg) => `${msg.attempts}/${msg.maxAttempts}`,
  },
  {
    id: 'createdAt',
    label: 'Data',
    render: (msg) => new Date(msg.createdAt).toLocaleString('pt-BR'),
  },
];

// ============================================================================
// COMPONENT
// ============================================================================

const WhatsAppConfigPage: React.FC = () => {
  const { manifesto } = useManifesto();
  const conectar = useAcao('whatsapp.configure', 'Conectar');
  const enviarTeste = useAcao('whatsapp.configure', 'Enviar Teste');
  const desconectar = useAcao('whatsapp.configure', 'Desconectar');
  const remover = useAcao('whatsapp.configure', 'Remover');
  const reenviar = useAcao('whatsapp.configure', 'Reenviar');
  const cancelar = useAcao('whatsapp.configure', 'Cancelar');
  const [activeTab, setActiveTab] = useState(0);
  const [instances, setInstances] = useState<WhatsAppInstance[]>([]);
  const [messages, setMessages] = useState<WhatsAppMessageItem[]>([]);
  const [failedCount, setFailedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create instance dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState('');
  const [selectedEntityType, setSelectedEntityType] = useState<'provider' | 'company' | 'global'>('provider');
  const [selectedProviderId, setSelectedProviderId] = useState<number | ''>('');
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | ''>('');
  const [providers, setProviders] = useState<Array<{ id: number; name: string }>>([]);
  const [companies, setCompanies] = useState<Array<{ id: number; name: string }>>([]);
  const [creating, setCreating] = useState(false);

  // QR dialog
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrInstanceId, setQrInstanceId] = useState<number | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [qrStatus, setQrStatus] = useState<WhatsAppInstanceStatus>('disconnected');
  const [qrExpired, setQrExpired] = useState(false);
  const qrPollingRef = useRef<NodeJS.Timeout | null>(null);
  const qrTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Test message dialog
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testInstanceId, setTestInstanceId] = useState<number | null>(null);
  const [sendingTest, setSendingTest] = useState(false);

  // Messages filter
  const [selectedInstanceId, setSelectedInstanceId] = useState<number | ''>('');
  const [messageStatusFilter, setMessageStatusFilter] = useState<string>('');

  // A instância global vale para todas as empresas, e criá-la é ação de plataforma — quem
  // responde por isso é o contexto ativo, não o papel de quem está logado. Papel é pacote de
  // permissão: perguntar por ele quebra no dia em que o cliente cria o próprio.
  const naPlataforma = manifesto?.perspectiva === 'plataforma';

  // ====================================================================
  // DATA FETCHING
  // ====================================================================

  const fetchInstances = useCallback(async () => {
    try {
      const data = await whatsappService.getInstances();
      setInstances(data);
      if (data.length > 0 && !selectedInstanceId) {
        setSelectedInstanceId(data[0]!.id);
      }
    } catch (err) {
      console.error('Error fetching instances:', err);
      setError('Erro ao carregar instâncias');
    }
  }, [selectedInstanceId]);

  const fetchMessages = useCallback(async () => {
    if (!selectedInstanceId) return;
    try {
      const result = await whatsappService.getMessages(
        selectedInstanceId as number,
        { status: messageStatusFilter || undefined, limit: 50 }
      );
      setMessages(result.data);
      const failed = result.data.filter((m) => m.status === 'failed').length;
      setFailedCount(failed);
    } catch (err) {
      console.error('Error fetching messages:', err);
    }
  }, [selectedInstanceId, messageStatusFilter]);

  const fetchProviders = useCallback(async () => {
    try {
      const data = await providerService.getProvidersForDropdown();
      setProviders(data);
    } catch (err) {
      console.error('Error fetching providers:', err);
    }
  }, []);

  const fetchCompanies = useCallback(async () => {
    try {
      const data = await companyService.getCompaniesForDropdown();
      setCompanies(data);
    } catch (err) {
      console.error('Error fetching companies:', err);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchInstances(), fetchProviders(), fetchCompanies()]);
      setLoading(false);
    };
    load();
  }, [fetchInstances, fetchProviders, fetchCompanies]);

  useEffect(() => {
    if (activeTab === 1 && selectedInstanceId) {
      fetchMessages();
    }
  }, [activeTab, selectedInstanceId, messageStatusFilter, fetchMessages]);

  // ====================================================================
  // QR CODE POLLING
  // ====================================================================

  const startQrPolling = (instanceId: number) => {
    stopQrPolling();
    setQrInstanceId(instanceId);
    setQrDialogOpen(true);
    setQrCode(null);
    setQrStatus('connecting');
    setQrExpired(false);

    // 90s timeout — after this, show expired state
    qrTimeoutRef.current = setTimeout(() => {
      stopQrPolling();
      setQrExpired(true);
    }, 90_000);

    const poll = async () => {
      try {
        const result = await whatsappService.getQR(instanceId);
        setQrCode(result.qr);
        setQrStatus(result.status);

        if (result.status === 'connected') {
          stopQrPolling();
          setQrDialogOpen(false);
          fetchInstances();
        }

        // Baileys closed the socket (internal QR expiry)
        if (result.status === 'disconnected') {
          stopQrPolling();
          setQrExpired(true);
        }
      } catch (err) {
        console.error('QR polling error:', err);
      }
    };

    poll();
    qrPollingRef.current = setInterval(poll, 2000);
  };

  const stopQrPolling = () => {
    if (qrPollingRef.current) {
      clearInterval(qrPollingRef.current);
      qrPollingRef.current = null;
    }
    if (qrTimeoutRef.current) {
      clearTimeout(qrTimeoutRef.current);
      qrTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    return () => stopQrPolling();
  }, []);

  // ====================================================================
  // ACTIONS
  // ====================================================================

  const handleConnect = async (instanceId: number) => {
    try {
      await whatsappService.connectInstance(instanceId);
      startQrPolling(instanceId);
    } catch (err) {
      setError('Erro ao iniciar conexão');
    }
  };

  const handleDisconnect = async (instanceId: number) => {
    try {
      await whatsappService.disconnectInstance(instanceId);
      fetchInstances();
    } catch (err) {
      setError('Erro ao desconectar');
    }
  };

  const handleDelete = async (instanceId: number) => {
    if (!confirm('Tem certeza que deseja remover esta instância? Os dados de autenticação serão apagados.')) return;
    try {
      await whatsappService.deleteInstance(instanceId);
      fetchInstances();
    } catch (err) {
      setError('Erro ao remover instância');
    }
  };

  const handleCreate = async () => {
    if (!newDisplayName.trim()) return;
    setCreating(true);
    try {
      if (selectedEntityType === 'global') {
        await whatsappService.createInstance({
          isGlobal: true,
          displayName: newDisplayName.trim(),
        });
      } else {
        const entityId = selectedEntityType === 'provider' ? selectedProviderId : selectedCompanyId;
        if (!entityId) return;
        await whatsappService.createInstance({
          entityType: selectedEntityType,
          entityId: entityId as number,
          displayName: newDisplayName.trim(),
        });
      }
      setCreateDialogOpen(false);
      setNewDisplayName('');
      setSelectedProviderId('');
      setSelectedCompanyId('');
      setSelectedEntityType('provider');
      fetchInstances();
    } catch (err: unknown) {
      setError((err instanceof Error ? err.message : '') || 'Erro ao criar instância');
    } finally {
      setCreating(false);
    }
  };

  const handleRetryMessage = async (instanceId: number, msgId: number) => {
    try {
      await whatsappService.retryMessage(instanceId, msgId);
      fetchMessages();
    } catch (err) {
      setError('Erro ao reenviar mensagem');
    }
  };

  const handleCancelMessage = async (instanceId: number, msgId: number) => {
    try {
      await whatsappService.cancelMessage(instanceId, msgId);
      fetchMessages();
    } catch (err) {
      setError('Erro ao cancelar mensagem');
    }
  };

  const handleSendTest = async () => {
    if (!testInstanceId || !testPhone.trim()) return;
    setSendingTest(true);
    try {
      await whatsappService.sendTestMessage(testInstanceId, testPhone.trim());
      setTestDialogOpen(false);
      setTestPhone('');
      setTestInstanceId(null);
      setError(null);
      alert('Mensagem de teste enviada com sucesso!');
    } catch (err: unknown) {
      setError((err instanceof Error ? err.message : '') || 'Erro ao enviar mensagem de teste');
    } finally {
      setSendingTest(false);
    }
  };

  const availableProviders = providers.filter(
    (p) => !instances.some((i) => i.entityType === 'provider' && i.entityId === p.id)
  );

  const availableCompanies = companies.filter(
    (c) => !instances.some((i) => i.entityType === 'company' && i.entityId === c.id)
  );

  const hasAvailableEntities = availableProviders.length > 0 || availableCompanies.length > 0;

  const hasOnlyProviders = availableProviders.length > 0 && availableCompanies.length === 0;
  const hasOnlyCompanies = availableCompanies.length > 0 && availableProviders.length === 0;
  const showEntityTypeRadio = !hasOnlyProviders && !hasOnlyCompanies;

  // Auto-select when opening the dialog with a single type/entity
  useEffect(() => {
    if (!createDialogOpen) return;
    if (hasOnlyProviders) {
      setSelectedEntityType('provider');
    } else if (hasOnlyCompanies) {
      setSelectedEntityType('company');
    }
  }, [createDialogOpen, hasOnlyProviders, hasOnlyCompanies]);

  // Auto-fill entity + displayName when only one option exists for the selected type
  useEffect(() => {
    if (!createDialogOpen) return;
    if (selectedEntityType === 'provider' && availableProviders.length === 1) {
      setSelectedProviderId(availableProviders[0]!.id);
      setNewDisplayName(availableProviders[0]!.name);
    } else if (selectedEntityType === 'company' && availableCompanies.length === 1) {
      setSelectedCompanyId(availableCompanies[0]!.id);
      setNewDisplayName(availableCompanies[0]!.name);
    }
  }, [createDialogOpen, selectedEntityType, availableProviders, availableCompanies]);

  // ====================================================================
  // TABLE ACTIONS
  // ====================================================================

  const instanceActions: DataTableAction<WhatsAppInstance>[] = [
    {
      icon: <QrCodeIcon fontSize="small" />,
      color: 'success',
      onClick: (inst) => handleConnect(inst.id),
      hidden: (inst) =>
        resolveInstanceStatus(inst) === 'connected' || (inst.isGlobal && !naPlataforma),
      ...conectar,
    },
    {
      icon: <SendIcon fontSize="small" />,
      color: 'primary',
      onClick: (inst) => {
        setTestInstanceId(inst.id);
        setTestPhone('');
        setTestDialogOpen(true);
      },
      hidden: (inst) => resolveInstanceStatus(inst) !== 'connected',
      ...enviarTeste,
    },
    {
      icon: <DisconnectIcon fontSize="small" />,
      color: 'warning',
      onClick: (inst) => handleDisconnect(inst.id),
      hidden: (inst) =>
        resolveInstanceStatus(inst) !== 'connected' || (inst.isGlobal && !naPlataforma),
      ...desconectar,
    },
    {
      icon: <DeleteIcon fontSize="small" />,
      color: 'error',
      onClick: (inst) => handleDelete(inst.id),
      hidden: (inst) => inst.isGlobal && !naPlataforma,
      ...remover,
    },
  ];

  const messageActions: DataTableAction<WhatsAppMessageItem>[] = [
    {
      icon: <RetryIcon fontSize="small" />,
      color: 'primary',
      onClick: (msg) => handleRetryMessage(msg.instanceId, msg.id),
      hidden: (msg) => msg.status !== 'failed',
      ...reenviar,
    },
    {
      icon: <CancelIcon fontSize="small" />,
      color: 'error',
      onClick: (msg) => handleCancelMessage(msg.instanceId, msg.id),
      hidden: (msg) => msg.status !== 'pending',
      ...cancelar,
    },
  ];

  // ====================================================================
  // RENDER
  // ====================================================================

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/home' },
        { label: 'Configurações' },
        { label: 'WhatsApp' },
      ]}
      title="WhatsApp"
      actions={
        <ActionButton
          acao="whatsapp.configure"
          rotulo="Nova Instância"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
          disabled={!hasAvailableEntities && !naPlataforma}
        >
          Nova Instância
        </ActionButton>
      }
    >
      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && (
        <>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Instâncias" icon={<InstanceIcon />} iconPosition="start" />
            <Tab
              icon={<MessageIcon />}
              iconPosition="start"
              label={
                <Badge badgeContent={failedCount} color="error" sx={{ pr: failedCount > 0 ? 1.5 : 0 }}>
                  Mensagens
                </Badge>
              }
            />
          </Tabs>

          {/* ============================================== */}
          {/* TAB 0: Instances                               */}
          {/* ============================================== */}
          <TabPanel value={activeTab} index={0}>
            <DataTable
              columns={instanceColumns}
              rows={instances}
              getRowId={(inst) => inst.id}
              emptyState={{
                title: 'Nenhuma instância WhatsApp configurada',
                description:
                  'As instâncias são conectadas nesta tela e é por elas que as mensagens saem.',
              }}
              actions={instanceActions}
            />
          </TabPanel>

          {/* ============================================== */}
          {/* TAB 1: Messages                                */}
          {/* ============================================== */}
          <TabPanel value={activeTab} index={1}>
            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 3 }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Instância</InputLabel>
                <Select
                  value={selectedInstanceId}
                  onChange={(e: SelectChangeEvent<number | ''>) => setSelectedInstanceId(e.target.value as number)}
                  label="Instância"
                >
                  {instances.map((inst) => (
                    <MenuItem key={inst.id} value={inst.id}>
                      {inst.displayName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={messageStatusFilter}
                  onChange={(e) => setMessageStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="pending">Pendentes</MenuItem>
                  <MenuItem value="sent">Enviadas</MenuItem>
                  <MenuItem value="failed">Falhas</MenuItem>
                  <MenuItem value="cancelled">Canceladas</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Messages table */}
            {selectedInstanceId ? (
              <DataTable
                columns={messageColumns}
                rows={messages}
                getRowId={(msg) => msg.id}
                emptyState={{
                  title: 'Nenhuma mensagem encontrada',
                  description:
                    'As mensagens são as que esta instância enviou ou tem na fila, dentro do filtro escolhido.',
                }}
                actions={messageActions}
              />
            ) : (
              <DataTable
                columns={messageColumns}
                rows={[]}
                getRowId={(msg) => msg.id}
                emptyState={{
                  title: 'Selecione uma instância para ver as mensagens',
                  description: 'O histórico fica guardado por instância; escolha uma no filtro acima.',
                }}
                actions={messageActions}
              />
            )}
          </TabPanel>
        </>
      )}

      {/* ============================================================ */}
      {/* CREATE INSTANCE DIALOG                                       */}
      {/* ============================================================ */}
      <Dialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          setNewDisplayName('');
          setSelectedProviderId('');
          setSelectedCompanyId('');
          setSelectedEntityType('provider');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Nova Instância WhatsApp</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {(showEntityTypeRadio || naPlataforma) && (
              <FormControl component="fieldset">
                <FormLabel component="legend">Vincular a</FormLabel>
                <RadioGroup
                  row
                  value={selectedEntityType}
                  onChange={(e) => {
                    const type = e.target.value as 'provider' | 'company' | 'global';
                    setSelectedEntityType(type);
                    setSelectedProviderId('');
                    setSelectedCompanyId('');
                    if (type === 'global') {
                      setNewDisplayName('');
                    } else if (type === 'provider' && availableProviders.length === 1) {
                      setSelectedProviderId(availableProviders[0]!.id);
                      setNewDisplayName(availableProviders[0]!.name);
                    } else if (type === 'company' && availableCompanies.length === 1) {
                      setSelectedCompanyId(availableCompanies[0]!.id);
                      setNewDisplayName(availableCompanies[0]!.name);
                    } else {
                      setNewDisplayName('');
                    }
                  }}
                >
                  {availableProviders.length > 0 && (
                    <FormControlLabel value="provider" control={<Radio />} label="Clínica (Prestador)" />
                  )}
                  {availableCompanies.length > 0 && (
                    <FormControlLabel value="company" control={<Radio />} label="Empresa" />
                  )}
                  {naPlataforma && (
                    <FormControlLabel value="global" control={<Radio />} label="Global" />
                  )}
                </RadioGroup>
              </FormControl>
            )}

            {selectedEntityType !== 'global' && (
              selectedEntityType === 'provider' ? (
                availableProviders.length === 1 ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <InstanceIcon color="primary" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Clínica
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {availableProviders[0]!.name}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <FormControl fullWidth>
                    <InputLabel>Clínica (Prestador)</InputLabel>
                    <Select
                      value={selectedProviderId}
                      onChange={(e: SelectChangeEvent<number | ''>) => {
                        const id = e.target.value as number;
                        setSelectedProviderId(id);
                        const match = availableProviders.find((p) => p.id === id);
                        if (match) setNewDisplayName(match.name);
                      }}
                      label="Clínica (Prestador)"
                    >
                      {availableProviders.map((p) => (
                        <MenuItem key={p.id} value={p.id}>
                          {p.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )
              ) : (
                availableCompanies.length === 1 ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <InstanceIcon color="info" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Empresa
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {availableCompanies[0]!.name}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <FormControl fullWidth>
                    <InputLabel>Empresa</InputLabel>
                    <Select
                      value={selectedCompanyId}
                      onChange={(e: SelectChangeEvent<number | ''>) => {
                        const id = e.target.value as number;
                        setSelectedCompanyId(id);
                        const match = availableCompanies.find((c) => c.id === id);
                        if (match) setNewDisplayName(match.name);
                      }}
                      label="Empresa"
                    >
                      {availableCompanies.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )
              )
            )}

            <TextField
              fullWidth
              label="Nome de exibição"
              placeholder="Ex: Recepção Clínica São Paulo"
              value={newDisplayName}
              onChange={(e) => setNewDisplayName(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCreateDialogOpen(false);
            setNewDisplayName('');
            setSelectedProviderId('');
            setSelectedCompanyId('');
            setSelectedEntityType('provider');
          }} disabled={creating}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={
              creating ||
              !newDisplayName.trim() ||
              (selectedEntityType !== 'global' && !(selectedEntityType === 'provider' ? selectedProviderId : selectedCompanyId))
            }
            startIcon={creating ? <CircularProgress size={20} /> : null}
          >
            {creating ? 'Criando...' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================================ */}
      {/* TEST MESSAGE DIALOG                                          */}
      {/* ============================================================ */}
      <Dialog
        open={testDialogOpen}
        onClose={() => {
          if (!sendingTest) {
            setTestDialogOpen(false);
            setTestPhone('');
            setTestInstanceId(null);
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Enviar Mensagem de Teste</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Telefone"
            placeholder="5511999999999"
            value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
            sx={{ mt: 1 }}
            helperText="Número com código do país (ex: 5511999999999)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)} disabled={sendingTest}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSendTest}
            disabled={sendingTest || !testPhone.trim()}
            startIcon={sendingTest ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {sendingTest ? 'Enviando...' : 'Enviar Teste'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================================ */}
      {/* QR CODE DIALOG                                               */}
      {/* ============================================================ */}
      <Dialog
        open={qrDialogOpen}
        onClose={() => {
          stopQrPolling();
          setQrDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WhatsAppIcon sx={{ color: '#25D366' }} />
          Conectar WhatsApp
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
            {qrStatus === 'connected' ? (
              <Box sx={{ textAlign: 'center' }}>
                <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" color="success.main">
                  Conectado com sucesso!
                </Typography>
              </Box>
            ) : qrExpired ? (
              <Box sx={{ textAlign: 'center' }}>
                <QrCodeIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  QR Code expirado
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  O tempo para escanear o QR Code esgotou. Gere um novo para tentar novamente.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<QrCodeIcon />}
                  onClick={() => {
                    if (qrInstanceId) handleConnect(qrInstanceId);
                  }}
                >
                  Gerar Novo QR Code
                </Button>
              </Box>
            ) : qrCode ? (
              <>
                <QRCodeSVG value={qrCode} size={256} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                  Abra o WhatsApp no seu celular &gt; Dispositivos Vinculados &gt; Vincular Dispositivo
                </Typography>
              </>
            ) : (
              <>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Gerando QR Code...
                </Typography>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              stopQrPolling();
              setQrDialogOpen(false);
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default WhatsAppConfigPage;
